import React from 'react';

const EMPTY_TIER = () => ({
    minAge: '',
    maxAge: '',
    quantity: '',
    price: '',
    status: 'ACTIVE',
});

function normalizeTierRow(row) {
    if (!row || typeof row !== 'object') return EMPTY_TIER();
    return {
        minAge: row.minAge ?? row.Min_Age ?? '',
        maxAge: row.maxAge ?? row.Max_Age ?? '',
        quantity: row.quantity ?? row.Quantity ?? '',
        price: row.price ?? row.Price ?? '',
        status: String(row.status ?? row.Status ?? 'ACTIVE').toUpperCase(),
    };
}

/**
 * Junior Eats age-band price/quantity editor (JREATS menu items).
 */
export default function AgePricingEditor({ value, onChange, disabled = false }) {
    const tiers = Array.isArray(value) ? value.map(normalizeTierRow) : [];

    const updateTier = (index, patch) => {
        const next = tiers.map((row, i) => (i === index ? { ...row, ...patch } : row));
        onChange?.(next);
    };

    const addTier = () => {
        onChange?.([...tiers, EMPTY_TIER()]);
    };

    const removeTier = (index) => {
        onChange?.(tiers.filter((_, i) => i !== index));
    };

    return (
        <div className="age-pricing-editor">
            <div className="age-pricing-editor__header">
                <label className="age-pricing-editor__title">Age-based portions (Junior Eats)</label>
                <p className="age-pricing-editor__hint">
                    Set quantity and price per age band. Base price/quantity above is used when no band matches.
                </p>
            </div>
            {tiers.length === 0 ? (
                <p className="age-pricing-editor__empty">No age bands yet. Add rows for LKG–Class 12 portions.</p>
            ) : (
                <div className="age-pricing-editor__table-wrap">
                    <table className="age-pricing-editor__table">
                        <thead>
                            <tr>
                                <th>Min age</th>
                                <th>Max age</th>
                                <th>Quantity</th>
                                <th>Price (₹)</th>
                                <th aria-label="Remove" />
                            </tr>
                        </thead>
                        <tbody>
                            {tiers.map((tier, index) => (
                                <tr key={`age-tier-${index}`}>
                                    <td>
                                        <input
                                            type="number"
                                            min="0"
                                            max="18"
                                            className="add-menu-form-field age-pricing-editor__input"
                                            value={tier.minAge}
                                            disabled={disabled}
                                            placeholder="3"
                                            onChange={(e) => updateTier(index, { minAge: e.target.value })}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            min="1"
                                            max="18"
                                            className="add-menu-form-field age-pricing-editor__input"
                                            value={tier.maxAge}
                                            disabled={disabled}
                                            placeholder="5"
                                            onChange={(e) => updateTier(index, { maxAge: e.target.value })}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text"
                                            className="add-menu-form-field age-pricing-editor__input age-pricing-editor__input--wide"
                                            value={tier.quantity}
                                            disabled={disabled}
                                            placeholder="200 ml"
                                            onChange={(e) => updateTier(index, { quantity: e.target.value })}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            className="add-menu-form-field age-pricing-editor__input"
                                            value={tier.price}
                                            disabled={disabled}
                                            placeholder="40"
                                            onChange={(e) => updateTier(index, { price: e.target.value })}
                                        />
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            className="age-pricing-editor__remove-btn"
                                            disabled={disabled}
                                            onClick={() => removeTier(index)}
                                        >
                                            Remove
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <button
                type="button"
                className="age-pricing-editor__add-btn"
                disabled={disabled}
                onClick={addTier}
            >
                + Add age band
            </button>
        </div>
    );
}

export function normalizeAgePricingForSubmit(rows) {
    if (!Array.isArray(rows)) return [];
    return rows
        .map((row) => normalizeTierRow(row))
        .filter((row) => row.minAge !== '' && row.maxAge !== '' && row.quantity && row.price !== '')
        .map((row) => ({
            minAge: Number.parseInt(row.minAge, 10),
            maxAge: Number.parseInt(row.maxAge, 10),
            quantity: String(row.quantity).trim(),
            price: Number.parseFloat(row.price),
            status: row.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
        }))
        .filter((row) => Number.isFinite(row.minAge) && Number.isFinite(row.maxAge) && Number.isFinite(row.price));
}

export function parseAgePricingFromItem(item) {
    const raw = item?.agePricing ?? item?.age_pricing ?? [];
    if (!Array.isArray(raw)) return [];
    return raw.map(normalizeTierRow);
}
